import { auth } from "./firebase/config.js";
import { MP_PRO_ONLY } from "./plans.js";

// Cobro del carrito con Mercado Pago (workflow "MiQRapp - Cobros con Mercado Pago" de
// n8n, ver n8n/miqrapp-cobros-mp.js). Cada negocio conecta su propia cuenta con OAuth:
// el cliente le paga directo al negocio y los tokens quedan solo en n8n, nunca acá.
const N8N = "https://n8n.miqrapp.com/webhook/miqrapp/";

// App "MiQRapp" de Mercado Pago Developers.
export const MP_APP_ID = "4333687511279609";
const AUTH_URL = "https://auth.mercadopago.com.ar/authorization";
const STATE_KEY = "miqrapp-mp-state";

// Tiene que estar cargada en la app de Mercado Pago (URLs de redireccionamiento) y en
// REDIRECTS de n8n/miqrapp-cobros-mp.js.
export const mpRedirectUri = () => window.location.origin + "/cuenta";

export const mpPlanOk = (business) => !MP_PRO_ONLY || business?.subscribed === true;

// El carrito ofrece pagar con Mercado Pago si el negocio conectó su cuenta y su plan lo incluye.
export const takesMpPayments = (business) => business?.mpConectado === true && mpPlanOk(business);

const post = async (path, body) => {
    const response = await fetch(N8N + path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });
    let data = {};
    try {
        data = await response.json();
    } catch (error) {
        // Sin cuerpo: queda el código de la respuesta.
    }
    if (!response.ok) {
        const error = new Error(data.error || `n8n respondió ${response.status}`);
        error.status = response.status;
        throw error;
    }
    return data;
}

// ---- Panel: conectar y desconectar la cuenta del negocio ----

const randomState = () => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// Lleva al dueño a Mercado Pago para que autorice a MiQRapp. Vuelve a /cuenta?code=…&state=…
export const startMpConnect = () => {
    const state = randomState();
    try {
        sessionStorage.setItem(STATE_KEY, state);
    } catch (error) {
        // Sin almacenamiento no se puede comprobar el regreso: igual se intenta.
    }
    const params = new URLSearchParams({
        client_id: MP_APP_ID,
        response_type: "code",
        platform_id: "mp",
        state,
        redirect_uri: mpRedirectUri()
    });
    window.location.href = AUTH_URL + "?" + params.toString();
}

// Lo que trae la dirección al volver de Mercado Pago, o null si no es un regreso de ahí.
// `valid` es false si el state no es el que guardamos al salir (otra pestaña, link viejo).
export const readMpReturn = (search) => {
    const params = new URLSearchParams(search);
    const code = params.get("code");
    const state = params.get("state");
    if (!state || (!code && !params.get("error"))) return null;
    let saved = null;
    try {
        saved = sessionStorage.getItem(STATE_KEY);
        sessionStorage.removeItem(STATE_KEY);
    } catch (error) {
        saved = null;
    }
    // Sin el state guardado no se acepta: si no, un link armado por otro podría conectar
    // al negocio una cuenta ajena y los cobros irían a esa cuenta.
    return { code, valid: !!code && saved === state };
}

export const finishMpConnect = async (code) => post("mp-conectar", {
    idToken: await auth.currentUser.getIdToken(),
    code,
    redirectUri: mpRedirectUri()
});

export const disconnectMp = async () => post("mp-desconectar", { idToken: await auth.currentUser.getIdToken() });

// ---- Menú: cobrar un pedido ya guardado ----

// Link de Checkout Pro del pedido. n8n arma el cobro con los precios de Firestore.
// Devuelve { initPoint } o { pagado: true } si el pedido ya estaba pagado.
export const createMpPayment = (usuarioEmpresa, numero) => post("mp-pagar", { usuarioEmpresa, numero, origen: window.location.origin });

// Le pregunta a Mercado Pago (vía n8n) cómo quedó el pago del pedido y lo guarda en el pedido.
// estado: "pagado", "pendiente", "rechazado", "devuelto", "sin-pago" o "sin-cuenta".
export const verifyMpPayment = (usuarioEmpresa, numero) => post("mp-verificar-pago", { usuarioEmpresa, numero });

// Mientras el cliente está en Mercado Pago, el pedido queda guardado en el navegador para
// poder mandarlo por WhatsApp cuando vuelve: { numero, message, initPoint }.
const pendingKey = (usuarioEmpresa) => "miqrapp-pago-" + usuarioEmpresa;

export const savePendingPayment = (usuarioEmpresa, pending) => {
    try {
        localStorage.setItem(pendingKey(usuarioEmpresa), JSON.stringify({ ...pending, at: Date.now() }));
    } catch (error) {
        // Sin almacenamiento, al volver igual se ve el estado del pago.
    }
}

export const readPendingPayment = (usuarioEmpresa) => {
    try {
        const saved = JSON.parse(localStorage.getItem(pendingKey(usuarioEmpresa)));
        // Más de dos días: ya no sirve (n8n tampoco cobra pedidos tan viejos).
        return saved && Date.now() - saved.at < 2 * 24 * 3600 * 1000 ? saved : null;
    } catch (error) {
        return null;
    }
}

export const clearPendingPayment = (usuarioEmpresa) => {
    try {
        localStorage.removeItem(pendingKey(usuarioEmpresa));
    } catch (error) {
        // Nada que hacer.
    }
}

// Texto del estado del pago para el panel y el menú.
export const PAYMENT_LABELS = {
    esperando: "Esperando pago",
    pendiente: "Pago en proceso",
    pagado: "Pagado",
    rechazado: "Pago rechazado",
    devuelto: "Pago devuelto"
};
